import { useState, useEffect } from 'react';
import { useRole } from '../App';
import { supabase } from '../utils/supabase';
import { getOperators } from '../utils/storage';
import { CATEGORIES, TIERS, scoreUnit, getTier } from '../utils/scoring';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS_OF_WEEK = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function SupplierDashboard() {
  const { userRecord } = useRole();
  const [operators, setOperators] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activations, setActivations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('portfolio');
  const [selectedOp, setSelectedOp] = useState(null);
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());

  useEffect(() => { loadAll(); }, [userRecord]);

  const loadAll = async () => {
    const [ops, pkgRes, actRes] = await Promise.all([
      getOperators(),
      supabase.from('bc_packages').select('*'),
      supabase.from('bc_activations').select('*').order('created_at', { ascending: false }),
    ]);
    const accessible = userRecord?.operator_ids?.length > 0
      ? ops.filter(o => userRecord.operator_ids.includes(o.id))
      : ops;
    setOperators(accessible);
    setPackages(pkgRes.data || []);
    setActivations(actRes.data || []);
    setLoading(false);
  };

  const allUnits = operators.flatMap(o => (o.data?.units || []).map(u => ({ ...u, operatorName: o.data?.operatorName || o.operator_name, opId: o.id })));
  const scored = allUnits.filter(u => +u.totalSales > 0).map(u => ({ ...u, results: scoreUnit(u) })).filter(u => u.results);

  // Tier distribution
  const tierDist = {};
  TIERS.forEach(t => { tierDist[t.name] = 0; });
  scored.forEach(u => u.results.forEach(r => { tierDist[r.tier.name]++; }));

  // Category averages
  const catTotals = {};
  CATEGORIES.forEach(c => { catTotals[c.id] = { count: 0, totalScore: 0 }; });
  scored.forEach(u => u.results.forEach(r => { catTotals[r.id].count++; catTotals[r.id].totalScore += r.finalScore; }));

  // Supplier's package commitments
  const supplierName = userRecord?.company_name || userRecord?.email || '';
  const myCommitments = [];
  packages.forEach(pkg => {
    (pkg.suppliers || []).forEach(sup => {
      if (sup.name?.toLowerCase() === supplierName.toLowerCase()) {
        const tier = TIERS[pkg.tier - 1];
        const cat = CATEGORIES.find(c => c.id === pkg.category);
        const isLiquor = pkg.category === 'spirits';
        const totalSkus = (pkg.suppliers || []).reduce((s, sp) => s + (sp.skuCount || 1), 0);
        const perSku = isLiquor && totalSkus > 0 ? pkg.monthly_total / totalSkus : 0;
        const monthlyCost = isLiquor ? Math.round(perSku * (sup.skuCount || 1)) : Math.round(pkg.monthly_total / (pkg.suppliers || []).length);
        myCommitments.push({
          category: cat, tier, tierNum: pkg.tier, quarter: pkg.quarter,
          skuCount: sup.skuCount, skus: sup.skus, status: sup.status,
          monthlyCost, quarterlyCost: monthlyCost * 3, totalPackage: pkg.monthly_total,
        });
      }
    });
  });

  // Calendar data
  const calActivations = activations.filter(a => {
    if (!a.data?.date) return false;
    const d = new Date(a.data.date + 'T12:00:00');
    return d.getMonth() === calMonth && d.getFullYear() === calYear;
  });

  const buildCalendar = () => {
    const firstDay = new Date(calYear, calMonth, 1);
    const lastDay = new Date(calYear, calMonth + 1, 0);
    const startPad = (firstDay.getDay() + 6) % 7;
    const days = [];
    for (let i = -startPad; i <= lastDay.getDate() + (6 - (lastDay.getDay() + 6) % 7); i++) {
      const date = new Date(calYear, calMonth, i + 1);
      const dateStr = date.toISOString().split('T')[0];
      const dayActs = calActivations.filter(a => a.data?.date === dateStr);
      days.push({ date, dateStr, day: date.getDate(), inMonth: date.getMonth() === calMonth, activations: dayActs });
    }
    return days;
  };

  const TABS = [
    { id: 'portfolio', label: 'Portfolio' },
    { id: 'commitments', label: `My Commitments (${myCommitments.length})` },
    { id: 'calendar', label: 'Activation Calendar' },
  ];

  if (loading) return <div className="py-20 text-center text-neutral-500">Loading portfolio...</div>;

  return (
    <div className="py-6">
      <div className="mb-6">
        <h1 className="font-display text-2xl text-white">Supplier Dashboard</h1>
        <p className="text-xs text-neutral-500">{supplierName} · {operators.length} operator(s) · {scored.length} unit(s)</p>
      </div>

      <div className="flex gap-1 mb-6 overflow-x-auto scrollbar-hide">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`whitespace-nowrap px-4 py-2 rounded-lg text-xs font-medium transition-all ${activeTab === tab.id ? 'bg-brand-gold/15 text-brand-gold' : 'text-neutral-500 hover:text-neutral-300'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Portfolio Tab */}
      {activeTab === 'portfolio' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
            {TIERS.map(tier => (
              <div key={tier.name} className="card p-4 text-center" style={{ borderTop: `3px solid ${tier.color}` }}>
                <p className="text-2xl font-black" style={{ color: tier.color }}>{tierDist[tier.name]}</p>
                <p className="text-[10px] text-neutral-500 uppercase tracking-wider">{tier.name}</p>
              </div>
            ))}
          </div>

          <div className="card p-5 mb-6">
            <h2 className="text-sm font-semibold text-brand-gold mb-4">Category Overview</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {CATEGORIES.map(cat => {
                const avg = catTotals[cat.id].count > 0 ? Math.round(catTotals[cat.id].totalScore / catTotals[cat.id].count) : 0;
                const tier = getTier(avg);
                return (
                  <div key={cat.id} className="text-center">
                    <p className="text-lg">{cat.icon}</p>
                    <p className="text-xs text-neutral-400 mt-1">{cat.name}</p>
                    <p className="text-2xl font-black mt-1" style={{ color: tier.color }}>{avg || '—'}</p>
                    <p className="text-[9px]" style={{ color: tier.color }}>{tier.name}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card p-5">
            <h2 className="text-sm font-semibold text-brand-gold mb-4">Operator Portfolio</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-neutral-800">
                  <th className="text-left py-2 pr-3 text-neutral-500">Operator</th>
                  <th className="text-left py-2 pr-3 text-neutral-500">Unit</th>
                  <th className="text-left py-2 pr-3 text-neutral-500">Market</th>
                  <th className="text-left py-2 pr-3 text-neutral-500">Venue</th>
                  {CATEGORIES.map(c => <th key={c.id} className="text-center py-2 px-2 text-neutral-500">{c.icon}</th>)}
                  <th className="text-center py-2 text-neutral-500">Activation</th>
                </tr></thead>
                <tbody>{scored.map((u, i) => (
                  <tr key={i} className="border-b border-neutral-800/30 hover:bg-neutral-800/20 cursor-pointer" onClick={() => setSelectedOp(selectedOp === i ? null : i)}>
                    <td className="py-2 pr-3 font-medium text-white">{u.operatorName}</td>
                    <td className="py-2 pr-3 text-neutral-400">{u.name}</td>
                    <td className="py-2 pr-3 text-neutral-500">{u.market}</td>
                    <td className="py-2 pr-3 text-neutral-600">{u.type}</td>
                    {u.results.map(r => (
                      <td key={r.id} className="text-center py-2 px-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-semibold ${r.tier.cls}`}>T{r.tier.num}</span>
                      </td>
                    ))}
                    <td className="text-center py-2">
                      <span className={`text-[9px] px-2 py-0.5 rounded font-medium ${u.activation === 'High' ? 'bg-emerald-500/15 text-emerald-400' : u.activation === 'Medium' ? 'bg-amber-500/15 text-amber-400' : 'bg-neutral-800 text-neutral-500'}`}>
                        {u.activation || 'Medium'}
                      </span>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>

          {selectedOp !== null && scored[selectedOp] && (
            <div className="card p-5 mt-4">
              <h2 className="text-sm font-semibold text-white mb-3">{scored[selectedOp].operatorName} — {scored[selectedOp].name}</h2>
              <p className="text-[10px] text-neutral-500 mb-4">{scored[selectedOp].market} · {scored[selectedOp].type} · {scored[selectedOp].cuisine}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {scored[selectedOp].results.map(r => (
                  <div key={r.id} className="bg-neutral-800/30 rounded-xl p-4 text-center" style={{ borderTop: `3px solid ${r.tier.color}` }}>
                    <p className="text-[10px] text-neutral-500 uppercase">{r.icon} {r.name}</p>
                    <p className="text-3xl font-black mt-1" style={{ color: r.tier.color }}>T{r.tier.num}</p>
                    <p className="text-xs font-semibold" style={{ color: r.tier.color }}>{r.tier.name}</p>
                    <p className="text-[10px] text-neutral-600 mt-1">Score: {r.finalScore}/100</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* My Commitments Tab */}
      {activeTab === 'commitments' && (
        <div>
          {myCommitments.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="card p-4 text-center">
                  <p className="text-2xl font-black text-white">{myCommitments.length}</p>
                  <p className="text-[10px] text-neutral-500">Active Commitments</p>
                </div>
                <div className="card p-4 text-center">
                  <p className="text-2xl font-black text-brand-gold">${myCommitments.reduce((s, c) => s + c.monthlyCost, 0).toLocaleString()}</p>
                  <p className="text-[10px] text-neutral-500">Monthly Total</p>
                </div>
                <div className="card p-4 text-center">
                  <p className="text-2xl font-black text-brand-gold">${myCommitments.reduce((s, c) => s + c.quarterlyCost, 0).toLocaleString()}</p>
                  <p className="text-[10px] text-neutral-500">Quarterly Total</p>
                </div>
                <div className="card p-4 text-center">
                  <p className="text-2xl font-black text-white">{myCommitments.reduce((s, c) => s + (c.skuCount || 0), 0) || myCommitments.length}</p>
                  <p className="text-[10px] text-neutral-500">{myCommitments.some(c => c.skuCount) ? 'Total SKUs' : 'Categories'}</p>
                </div>
              </div>

              <div className="card p-5">
                <h2 className="text-sm font-semibold text-brand-gold mb-4">Your Commitments by Tier</h2>
                <div className="space-y-3">
                  {myCommitments.map((c, i) => (
                    <div key={i} className="flex items-center gap-4 bg-neutral-800/20 rounded-lg p-4" style={{ borderLeft: `4px solid ${c.tier?.color || '#666'}` }}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm">{c.category?.icon}</span>
                          <span className="text-xs font-semibold text-white">{c.category?.name}</span>
                          <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium`} style={{ backgroundColor: (c.tier?.color || '#666') + '20', color: c.tier?.color }}>{c.tier?.name} (Tier {c.tierNum})</span>
                          <span className="text-[9px] text-neutral-600">{c.quarter}</span>
                        </div>
                        {c.skuCount && <p className="text-[10px] text-neutral-500">{c.skuCount} SKU(s){c.skus ? ` — ${c.skus}` : ''}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-white">${c.monthlyCost.toLocaleString()}/mo</p>
                        <p className="text-[9px] text-neutral-500">${c.quarterlyCost.toLocaleString()}/qtr</p>
                      </div>
                      <span className={`text-[9px] px-2 py-1 rounded font-medium ${c.status === 'active' || c.status === 'committed' ? 'bg-emerald-500/15 text-emerald-400' : c.status === 'declined' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                        {c.status || 'proposed'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="card p-8 text-center">
              <p className="text-neutral-500">No package commitments found for {supplierName}.</p>
              <p className="text-neutral-600 text-xs mt-2">Contact Next Experience Group to discuss participation.</p>
            </div>
          )}
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (() => {
        const calDays = buildCalendar();
        const weeks = [];
        for (let i = 0; i < calDays.length; i += 7) weeks.push(calDays.slice(i, i + 7));

        return (
          <div>
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); }} className="btn-ghost text-xs">← Prev</button>
              <h2 className="text-sm font-semibold text-white">{MONTHS[calMonth]} {calYear}</h2>
              <button onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); }} className="btn-ghost text-xs">Next →</button>
            </div>

            <div className="card p-4">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {DAYS_OF_WEEK.map(d => <div key={d} className="text-[9px] text-neutral-600 text-center font-medium py-1">{d}</div>)}
              </div>
              {weeks.map((week, wi) => (
                <div key={wi} className="grid grid-cols-7 gap-1">
                  {week.map((day, di) => (
                    <div key={di} className={`min-h-[80px] rounded-lg p-1.5 ${day.inMonth ? 'bg-neutral-800/30' : 'bg-neutral-900/20 opacity-40'}`}>
                      <p className={`text-[9px] mb-1 ${day.dateStr === new Date().toISOString().split('T')[0] ? 'text-brand-gold font-bold' : 'text-neutral-500'}`}>{day.day}</p>
                      {day.activations.map((a, ai) => {
                        const cats = Array.isArray(a.data?.categories) ? a.data.categories : (a.data?.category ? [a.data.category] : []);
                        const icons = cats.map(cid => CATEGORIES.find(c => c.id === cid)?.icon).filter(Boolean).join('') || '📋';
                        return (
                          <div key={ai} className={`text-[7px] px-1 py-0.5 rounded mb-0.5 truncate ${a.data?.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400' : a.data?.status === 'active' ? 'bg-blue-500/15 text-blue-400' : 'bg-amber-500/15 text-amber-400'}`}
                            title={`${a.data?.name} — ${a.data?.unit || ''}`}>
                            {icons} {a.data?.type || a.data?.name}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Activation list for this month */}
            {calActivations.length > 0 && (
              <div className="card p-5 mt-4">
                <h2 className="text-sm font-semibold text-brand-gold mb-3">{MONTHS[calMonth]} Activations ({calActivations.length})</h2>
                <div className="space-y-2">
                  {calActivations.sort((a, b) => (a.data?.date || '').localeCompare(b.data?.date || '')).map(a => {
                    const cats = Array.isArray(a.data?.categories) ? a.data.categories : (a.data?.category ? [a.data.category] : []);
                    const icons = cats.map(cid => CATEGORIES.find(c => c.id === cid)?.icon).filter(Boolean).join(' ') || '📋';
                    return (
                      <div key={a.id} className="flex items-center gap-3 bg-neutral-800/20 rounded-lg p-3">
                        <span className="text-base">{icons}</span>
                        <div className="flex-1">
                          <p className="text-xs font-medium text-white">{a.data?.type || a.data?.name}</p>
                          <p className="text-[10px] text-neutral-500">
                            {a.data?.unit ? `${a.data.unit} · ` : ''}
                            {a.data?.date ? new Date(a.data.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                          </p>
                        </div>
                        <span className={`text-[9px] px-2 py-0.5 rounded font-medium ${a.data?.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400' : a.data?.status === 'active' ? 'bg-blue-500/15 text-blue-400' : 'bg-amber-500/15 text-amber-400'}`}>
                          {a.data?.status || 'planned'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {operators.length === 0 && activeTab === 'portfolio' && (
        <div className="card p-8 text-center">
          <p className="text-neutral-500">No operators assigned to your account yet.</p>
          <p className="text-neutral-600 text-xs mt-2">Contact Next Experience Group to get access.</p>
        </div>
      )}

      <p className="text-center text-[8px] text-neutral-700 mt-8">NEG Proprietary Category Support Scoring System · Revenue data is not displayed</p>
    </div>
  );
}
